/**
 * /admin/scheduled-jobs/:jobId — per-job detail page (#1278 / #1280).
 *
 * Three tabs:
 *   1. Overview — current schedule, alerts, defined-in-code card,
 *      most-recent run's log excerpt + "View full log" link.
 *   2. Run history — paginated table of every recorded ScheduledJobRun.
 *   3. Configuration — same fields as the Configure dialog, surfaced
 *      inline so an operator can edit without opening a modal.
 *
 * Every timestamp renders through `useUserTimezone()`. The "Pause /
 * Resume" + "Run now" header actions mirror the catalog row's overflow
 * menu so the operator never has to bounce between pages.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { useToast } from '@/hooks/useToast'
import { formatLocalDateTime, formatRelativeTime } from '@/lib/utils'
import {
  adminScheduledJobsAPI,
  type ScheduledJobDetailResponse,
  type ScheduledJobRow,
  type ScheduledJobRunRow,
} from '@/lib/api'
import { ScheduledJobConfigureDialog } from './ConfigureDialog'

export function ScheduledJobDetailPage() {
  const { jobId } = useParams<{ jobId: string }>()
  const navigate = useNavigate()
  const tz = useUserTimezone()
  const toast = useToast()
  const [data, setData] = useState<ScheduledJobDetailResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [configureOpen, setConfigureOpen] = useState(false)

  const reload = useCallback(async () => {
    if (!jobId) return
    try {
      setLoading(true)
      const res = await adminScheduledJobsAPI.get(jobId)
      setData(res)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load this scheduled job.')
    } finally {
      setLoading(false)
    }
  }, [jobId])

  useEffect(() => {
    void reload()
  }, [reload])

  async function handleRunNow() {
    if (!data?.job) return
    try {
      const { runId } = await adminScheduledJobsAPI.runNow(data.job.jobId)
      toast({
        variant: 'success',
        title: `Run started`,
        description: (
          <span>
            Started a run of <strong>{data.job.displayName}</strong>. Run id:{' '}
            <code className="text-[11px]">{runId}</code>.{' '}
            <Link
              to={`/admin/audit?action=scheduledJob.run_now&targetId=${encodeURIComponent(data.job.jobId)}`}
              className="underline"
            >
              View in audit log
            </Link>
          </span>
        ),
      })
      void reload()
    } catch (err) {
      toast({
        variant: 'error',
        title: 'Could not start a run',
        description: err instanceof Error ? err.message : 'Unknown error.',
      })
    }
  }

  async function handleStatusChange(nextStatus: 'active' | 'paused' | 'disabled') {
    if (!data?.job) return
    try {
      const updated = await adminScheduledJobsAPI.patch(data.job.jobId, { status: nextStatus })
      setData({ ...data, job: updated })
      const verb = nextStatus === 'active' ? 'Resumed' : nextStatus === 'paused' ? 'Paused' : 'Disabled'
      const auditAction =
        nextStatus === 'active'
          ? 'scheduledJob.resumed'
          : nextStatus === 'paused'
            ? 'scheduledJob.paused'
            : 'scheduledJob.disabled'
      toast({
        variant: 'success',
        title: `${verb} ${updated.displayName}`,
        description: (
          <Link
            to={`/admin/audit?action=${auditAction}&targetId=${encodeURIComponent(updated.jobId)}`}
            className="underline"
          >
            View in audit log
          </Link>
        ),
      })
    } catch (err) {
      toast({
        variant: 'error',
        title: 'Could not change status',
        description: err instanceof Error ? err.message : 'Unknown error.',
      })
    }
  }

  async function handleDelete() {
    if (!data?.job) return
    if (!window.confirm(`Delete the catalog entry for "${data.job.displayName}"? This cannot be undone.`)) {
      return
    }
    try {
      await adminScheduledJobsAPI.delete(data.job.jobId)
      toast({ variant: 'success', title: `Removed ${data.job.displayName}` })
      navigate('/admin/scheduled-jobs')
    } catch (err) {
      toast({
        variant: 'error',
        title: 'Could not delete',
        description: err instanceof Error ? err.message : 'Unknown error.',
      })
    }
  }

  if (!jobId) {
    return <p className="text-sm text-destructive">No job id in the URL.</p>
  }

  return (
    <div className="space-y-6 max-w-full">
      <div className="text-xs text-muted-foreground">
        <Link to="/admin" className="hover:text-foreground">
          Global Admin
        </Link>
        {' · '}
        <Link to="/admin/scheduled-jobs" className="hover:text-foreground">
          Scheduled Jobs
        </Link>
        {' · '}
        <span>{data?.job.displayName ?? jobId}</span>
      </div>

      {loading && !data && <p className="text-sm text-muted-foreground">Loading scheduled job…</p>}
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      {data && (
        <>
          {!data.job.registered && (
            <div className="rounded border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-950/30 p-3 text-sm">
              <strong>No longer registered in code.</strong> The runner will skip this job until it
              comes back in a deploy. You can remove the catalog entry below.
            </div>
          )}

          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold">{data.job.displayName}</h1>
              <p className="text-sm text-muted-foreground mt-1">{data.job.description}</p>
            </div>
            <div className="flex gap-2">
              {data.job.registered && data.job.status === 'active' && (
                <Button variant="outline" onClick={() => handleStatusChange('paused')}>
                  Pause
                </Button>
              )}
              {data.job.registered && data.job.status === 'paused' && (
                <Button variant="outline" onClick={() => handleStatusChange('active')}>
                  Resume
                </Button>
              )}
              {data.job.registered && data.job.status === 'disabled' && (
                <Button variant="outline" onClick={() => handleStatusChange('active')}>
                  Re-enable
                </Button>
              )}
              {data.job.registered && (
                <Button onClick={handleRunNow} disabled={data.job.runInProgress}>
                  Run now
                </Button>
              )}
              {!data.job.registered && (
                <Button variant="destructive" onClick={handleDelete}>
                  Delete
                </Button>
              )}
            </div>
          </div>

          <Tabs defaultValue="overview">
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="history">Run history</TabsTrigger>
              <TabsTrigger value="configuration">Configuration</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-4">
              <OverviewTab job={data.job} recentRuns={data.recentRuns} tz={tz} />
            </TabsContent>

            <TabsContent value="history" className="mt-4">
              <RunHistoryTab initialRuns={data.recentRuns} tz={tz} />
            </TabsContent>

            <TabsContent value="configuration" className="mt-4">
              <ConfigurationTab
                job={data.job}
                onOpenDialog={() => setConfigureOpen(true)}
              />
            </TabsContent>
          </Tabs>
        </>
      )}

      {configureOpen && data?.job && (
        <ScheduledJobConfigureDialog
          jobId={data.job.jobId}
          open
          onClose={() => setConfigureOpen(false)}
          onSaved={(updated) => {
            setData({ ...data, job: updated })
            setConfigureOpen(false)
            toast({
              variant: 'success',
              title: `Saved changes to ${updated.displayName}`,
              description: (
                <Link
                  to={`/admin/audit?action=scheduledJob.config_updated&targetId=${encodeURIComponent(updated.jobId)}`}
                  className="underline"
                >
                  View in audit log
                </Link>
              ),
            })
          }}
        />
      )}
    </div>
  )
}

function OverviewTab({
  job,
  recentRuns,
  tz,
}: {
  job: ScheduledJobRow
  recentRuns: ScheduledJobRunRow[]
  tz: string
}) {
  const lastRun = recentRuns[0]
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 space-y-4">
        {lastRun && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Last run · {formatLocalDateTime(lastRun.startedAt, tz)}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <RunStatusBadge status={lastRun.status} />
                {lastRun.completedAt && (
                  <span className="text-muted-foreground">
                    Took {Math.max(1, Math.round((new Date(lastRun.completedAt).getTime() - new Date(lastRun.startedAt).getTime()) / 1000))}s
                  </span>
                )}
                {lastRun.rowsTouched !== null && (
                  <span className="text-muted-foreground">· {lastRun.rowsTouched} row(s) touched</span>
                )}
              </div>
              {lastRun.errorSummary && (
                <div role="alert" className="text-xs text-destructive whitespace-pre-wrap">
                  {lastRun.errorSummary}
                </div>
              )}
              {lastRun.logExcerpt && (
                <pre className="bg-muted/50 text-xs p-3 rounded font-mono max-h-72 overflow-auto whitespace-pre-wrap">
                  {lastRun.logExcerpt}
                </pre>
              )}
              {lastRun.logRef && isSafeUrl(lastRun.logRef) && (
                <a
                  href={lastRun.logRef}
                  className="text-xs underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View full log in Phoenix →
                </a>
              )}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent runs</CardTitle>
          </CardHeader>
          <CardContent>
            {recentRuns.length === 0 ? (
              <p className="text-sm text-muted-foreground">No runs recorded yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Run id</TableHead>
                    <TableHead>Started</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Trigger</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentRuns.map((r) => (
                    <TableRow key={r.runId}>
                      <TableCell>
                        <code className="text-[11px]">{r.runId.slice(0, 8)}</code>
                      </TableCell>
                      <TableCell>
                        <div className="text-xs">{formatLocalDateTime(r.startedAt, tz)}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {formatRelativeTime(new Date(r.startedAt))}
                        </div>
                      </TableCell>
                      <TableCell>
                        {r.completedAt ? (
                          <span className="text-xs">
                            {Math.max(1, Math.round((new Date(r.completedAt).getTime() - new Date(r.startedAt).getTime()) / 1000))}s
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">running…</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <RunStatusBadge status={r.status} />
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{describeTrigger(r.trigger)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Schedule</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <code className="text-xs">{job.cron}</code>
            <div className="text-xs text-muted-foreground">in {job.timezone}</div>
            {job.nextRunAt && job.status === 'active' && (
              <div className="text-xs">
                Next: {formatLocalDateTime(job.nextRunAt, tz)}{' '}
                <span className="text-muted-foreground">
                  ({formatRelativeTime(new Date(job.nextRunAt))})
                </span>
              </div>
            )}
            {job.status !== 'active' && (
              <div className="text-xs text-muted-foreground">Status: {job.status}</div>
            )}
            <div className="text-[11px] text-muted-foreground pt-2 border-t mt-2">
              Stored in UTC; displayed in your preferred time zone ({tz}).
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Alerts</CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            <div>On failure: <strong>{describeAlert(job.onFailure)}</strong></div>
            <div className="text-xs text-muted-foreground mt-1">
              Max run duration: {job.maxDurationSec}s. Runs that exceed this are stopped and the
              run record marks them as timed out.
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Defined in code</CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-muted-foreground">
            <p>
              This job's body lives in the source code and is reviewed via PR. The admin surface
              only changes <em>when</em> it runs and how alerts route — not what it does.
            </p>
            <p className="mt-2">
              Edit policy:{' '}
              <Badge variant="secondary" className="text-[10px]">
                {job.editPolicy === 'code-owned-schedule' ? 'Code-owned schedule' : 'Editable schedule'}
              </Badge>
            </p>
            <p className="mt-2">
              Definition hash: <code>{job.definitionHash}</code>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function RunHistoryTab({
  initialRuns,
  tz,
}: {
  initialRuns: ScheduledJobRunRow[]
  tz: string
}) {
  // Slice 1's API returns "recent ~10" on the detail endpoint. For
  // slice 2 we surface the initial 10 here; a future enhancement
  // wires a dedicated paginated endpoint. The header documents the
  // current scope so operators don't expect deeper history.
  const [runs] = useState<ScheduledJobRunRow[]>(initialRuns)
  const [statusFilter, setStatusFilter] = useState<'all' | ScheduledJobRunRow['status']>('all')
  const filtered = useMemo(
    () => (statusFilter === 'all' ? runs : runs.filter((r) => r.status === statusFilter)),
    [runs, statusFilter],
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Run history</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          <label className="text-xs text-muted-foreground">Filter:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | ScheduledJobRunRow['status'])}
            className="rounded border bg-background text-xs px-2 py-1"
          >
            <option value="all">All runs</option>
            <option value="succeeded">Succeeded</option>
            <option value="failed">Failed</option>
            <option value="timed-out">Timed out</option>
            <option value="running">Running</option>
          </select>
        </div>
        {filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground">No runs match this filter.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Run id</TableHead>
                <TableHead>Started</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Metrics</TableHead>
                <TableHead>Trigger</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.runId}>
                  <TableCell>
                    <code className="text-[11px]">{r.runId.slice(0, 8)}</code>
                  </TableCell>
                  <TableCell>
                    <div className="text-xs">{formatLocalDateTime(r.startedAt, tz)}</div>
                  </TableCell>
                  <TableCell>
                    {r.completedAt ? (
                      <span className="text-xs">
                        {Math.max(1, Math.round((new Date(r.completedAt).getTime() - new Date(r.startedAt).getTime()) / 1000))}s
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">running…</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <RunStatusBadge status={r.status} />
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {r.metrics ? renderMetrics(r.metrics) : '—'}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{describeTrigger(r.trigger)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <p className="text-[11px] text-muted-foreground">
          Showing the most-recent runs returned by the detail endpoint. A paginated
          history view backed by a dedicated endpoint lands in a follow-up; until then this tab
          reflects the same recent-runs slice the Overview tab uses.
        </p>
      </CardContent>
    </Card>
  )
}

function ConfigurationTab({
  job,
  onOpenDialog,
}: {
  job: ScheduledJobRow
  onOpenDialog: () => void
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Configuration</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-muted-foreground">
          Use the editor below to change the cadence, time zone, and failure-alert routing. The job
          identity (name, description, max duration) is owned by code and refreshed on every
          deploy.
        </p>
        <Button onClick={onOpenDialog}>Open editor</Button>
        <div className="text-xs text-muted-foreground space-y-1 mt-3 pt-3 border-t">
          <div>
            Current cadence: <code>{job.cron}</code> in {job.timezone}
          </div>
          <div>Status: {job.status}</div>
          <div>On failure: {describeAlert(job.onFailure)}</div>
        </div>
      </CardContent>
    </Card>
  )
}

/** Allow only http / https schemes when rendering a stored log URL as
 * a clickable link. Server data is generally trusted, but a defensive
 * scheme allowlist keeps a future buggy producer from injecting a
 * `javascript:` / `data:` URL into the operator's clickable surface.
 * PR #1283 Copilot review. */
function isSafeUrl(href: string): boolean {
  try {
    const url = new URL(href, window.location.href)
    return url.protocol === 'https:' || url.protocol === 'http:'
  } catch {
    return false
  }
}

/** Map the raw trigger enum to plain-language labels the operator
 * reads in the Recent runs + Run history tables. PR #1283 Copilot
 * review — `boot-claim-release` and the bare `run-now` token are
 * internal jargon. */
function describeTrigger(trigger: ScheduledJobRunRow['trigger']): string {
  switch (trigger) {
    case 'scheduled':
      return 'On schedule'
    case 'run-now':
      return 'Run now'
    case 'boot-claim-release':
      return 'Boot cleanup'
  }
}

function RunStatusBadge({ status }: { status: ScheduledJobRunRow['status'] }) {
  switch (status) {
    case 'succeeded':
      return <Badge variant="default">Succeeded</Badge>
    case 'failed':
      return <Badge variant="destructive">Failed</Badge>
    case 'timed-out':
      return <Badge variant="destructive">Timed out</Badge>
    case 'running':
      return <Badge variant="secondary">Running</Badge>
  }
}

function describeAlert(onFailure: ScheduledJobRow['onFailure']): string {
  switch (onFailure) {
    case 'silent':
      return 'Silent (only the run record carries the failure)'
    case 'email':
      return 'Email someone'
    case 'slack':
      return 'Post to Slack'
  }
}

function renderMetrics(metrics: Record<string, unknown>): string {
  return Object.entries(metrics)
    .map(([k, v]) => `${k}=${String(v)}`)
    .join(' · ')
}
